import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faRoute, faCalendarDay, faEye } from '@/fontawesome';
import TradingRecommendationsStats from '@/components/dashboard/TradingRecommendationsStats';
import RecentActivityFeed from '@/components/dashboard/RecentActivityFeed';
import RecentVideosCard from '@/components/dashboard/RecentVideosCard';
import TrafficLightCard from '@/components/dashboard/TrafficLightCard';
import UpcomingEventsCard from '@/components/dashboard/UpcomingEventsCard';
import AcademyProgressCard from '@/components/dashboard/AcademyProgressCard';
import JournalCard from '@/components/dashboard/JournalCard';
import SignalModal from '@/components/utils/SignalModal.tsx';
import PromotionModal from '@/components/dashboard/PromotionModal';
import { useUser } from '@/contexts/UserContext';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useTourContext } from '@/components/onboarding/TourProvider';
import { usePromotionModal } from '@/hooks/usePromotionModal';
import BottomMarketTicker from '@/components/dashboard/BottomMarketTicker';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError } from '@/utils/toasts';
import type { GetDashboardResponse } from '@/modelsAPI';
import { PROMOTION_CONFIG } from '@/consts/promotion';
import FullscreenError from '@/components/utils/FullscreenError';

export default function Dashboard() {
  const [isSignalModalOpen, setIsSignalModalOpen] = useState(false);
  const { user, isAdmin } = useUser();
  const { hasFeature } = useFeatureAccess();
  const { startTour } = useTourContext();
  const { isOpen: isPromotionOpen, handleClose: handlePromotionClose } = usePromotionModal(
    PROMOTION_CONFIG,
    user?.featureConfig ?? null
  );
  const [isAdminPromotionPreviewOpen, setIsAdminPromotionPreviewOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState<GetDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await APIRoutes.getDashboard();
      setDashboardData(data);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Dashboard konnte nicht geladen werden');
      handleAPIError(err, 'Das Dashboard konnte nicht geladen werden. Bitte lade die Seite neu oder versuche es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error !== null || dashboardData === null) {
    return (
      <FullscreenError
        title="Dashboard nicht verfügbar"
        message={error || 'Das Dashboard konnte nicht geladen werden.'}
        onRetry={fetchDashboardData}
      />
    );
  }

  return (
    <>
      <div className="text-center">
            <div className="mb-8">
              {/*<img
                key={isDarkMode ? 'logo-light' : 'logo-dark'}
                src={isDarkMode ? "/logo_light.png" : "/logo.png"}
                alt="Börsencampus Logo"
                className="h-32 object-contain mx-auto mb-6 mt-16"
              />*/}
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Willkommen zurück {user?.firstName ?? 'zurück'}!
              </h2>

              {/* Schnellzugriff-Buttons (Tour + Promotion nur für Admins) */}
              <div className="flex flex-wrap justify-center gap-3">
                {isAdmin && (
                  <button
                    onClick={startTour}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-colors cursor-pointer font-medium"
                  >
                    <FontAwesomeIcon icon={faRoute} className="w-4 h-4 mr-2" />
                    Tour starten
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setIsAdminPromotionPreviewOpen(true)}
                    className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 text-white rounded-lg transition-colors cursor-pointer font-medium"
                  >
                    <FontAwesomeIcon icon={faEye} className="w-4 h-4 mr-2" />
                    Promotion Modal
                  </button>
                )}
                {user !== null && !user.featureConfig.communityMember && (
                  <a
                    data-tour="beratungstermin-btn"
                    href="https://calendly.com/academy-svenbolz/trading-academy-kennenlerngesprach"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition-colors cursor-pointer font-medium"
                  >
                    <FontAwesomeIcon icon={faCalendarDay} className="w-4 h-4 mr-2" />
                    Strategiegespräch vereinbaren
                  </a>
                )}
              </div>
            </div>

            {/* Dashboard Content */}
            <div className="mb-8 max-w-7xl mx-auto">
              {/* Shared Grid Layout for all dashboard cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 auto-rows-[320px] gap-4">
                {/* Trading Stats - cards render directly into grid */}
                <TradingRecommendationsStats
                  coreRecommendations={dashboardData.coreRecommendations ?? []}
                  earningsRecommendations={dashboardData.earningsRecommendations ?? []}
                  swingTradeRecommendations={dashboardData.swingTradeRecommendations ?? []}
                  longTermRecommendations={dashboardData.longTermRecommendations ?? []}
                  finishedSwingTradeRecommendations={dashboardData.finishedSwingTradeRecommendations ?? []}
                  finishedLongTermRecommendations={dashboardData.finishedLongTermRecommendations ?? []}
                />

                {/* Recent Activity Feed - 2x1: spans 2 columns and 1 row on md+ screens */}
                <div data-tour="recent-activities" className="col-span-1 md:col-span-2 row-span-1">
                  <RecentActivityFeed activities={dashboardData.activities ?? []} />
                </div>

                {/* Recent Videos Card - 2x1: spans 2 columns and 1 row on md+ screens */}
                <div data-tour="latest-videos" className="col-span-1 md:col-span-2 row-span-1">
                  <RecentVideosCard videos={dashboardData.lastPublishedVideos ?? []} />
                </div>

                {/* Traffic Light Card - 1x1 card */}
                <div data-tour="market-indicator">
                  <TrafficLightCard stockStatusLight={dashboardData.stockStatusLight ?? null} />
                </div>

                {/* Upcoming Events Card - 2x1: spans 2 columns and 1 row on md+ screens */}
                <div className="col-span-1 md:col-span-2 row-span-1">
                  <UpcomingEventsCard calendar={dashboardData.calendar ?? []} />
                </div>

                {/* Academy Progress Card - 1x1 card */}
                <AcademyProgressCard lastViewedVideos={dashboardData.lastViewedVideos ?? []} />

                {/* Trading Journal - 1x1 card, only for users with the feature */}
                {hasFeature('journal') && <JournalCard />}
              </div>
            </div>
          </div>

      {/* Signal Modal */}
      <SignalModal
        isOpen={isSignalModalOpen}
        onClose={() => setIsSignalModalOpen(false)}
      />
      <PromotionModal
        config={PROMOTION_CONFIG}
        isOpen={isPromotionOpen || isAdminPromotionPreviewOpen}
        onClose={() => {
          handlePromotionClose();
          setIsAdminPromotionPreviewOpen(false);
        }}
      />
      <BottomMarketTicker />
    </>
  )
}
